import React, { useCallback, useRef, useEffect, useState } from "react";
import {
  Box,
  Button,
  Icons,
  Modal,
  Radio,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import { DocInfo, FolderInfoDTO } from "../../types/files";
import useCheckboxGroup from "fds/src/hooks/useCheckboxGroup";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import { nanoid } from "@reduxjs/toolkit";
import { t } from "i18next";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
interface FolderMoveModalProps {
  open: boolean;
  onClose: () => void;
  docInfo?: DocInfo[]; // 이동하고 싶은 문서들
}
const FolderMoveModal: React.FC<FolderMoveModalProps> = ({
  open,
  onClose,
  docInfo,
}) => {
  const urlParmas = new URLSearchParams(window.location.search);

  /** Hook 정리 */
  const toasts = useToast();
  const checkboxGroup = useCheckboxGroup<string>();
  const { workgroupState } = useWorkgroupStore();
  const { documentState, getFolderInfo, updateFileIntoOtherFolder } =
    useDocumentStore();

  /** State 정리 */
  const [folderInfo, changeFolderInfo] = useState<FolderInfoDTO[]>(); // 폴터 리스트 -> flat한 형태
  const [currentFolderId, changeCurrFolderId] = useState(""); // 현재 폴더 위치
  const [selectFolderId, changeSelectFolderId] = useState<string>(); // 선택된 폴더 위치
  const [workgroupId, changeWorkgroupId] = useState("");

  /** UseEffect 정리 */
  useEffect(() => {
    if (
      !docInfo ||
      (folderInfo &&
        docInfo[0]?.workgroup.workgroupId === folderInfo[0].folderId)
    )
      return;
    changeWorkgroupId(docInfo[0]?.workgroup.workgroupId);
    getFolderInfo(docInfo[0]?.workgroup.workgroupId);
  }, [docInfo]);
  useEffect(() => {
    const givenFolderId = urlParmas.get("folderId") ?? workgroupId;
    changeCurrFolderId(givenFolderId ? decodeURI(givenFolderId) : "");
  }, [urlParmas]);
  useEffect(() => {
    changeFolderInfo(documentState.folderList);
  }, [documentState]);

  /** 함수 정리 */
  /** 문서를 대상 폴더로 이동 */
  const handleMoverFileToOtherFolder = async () => {
    if (!docInfo) return;
    let resp: any = await updateFileIntoOtherFolder({
      workgroupId: workgroupId,
      folderId: selectFolderId,
      syncIds: docInfo.map((item) => item.syncId),
    });
    onClose();
    if (resp.payload) {
      toasts.toastMsg(nanoid(), t("common.alert.moveFolderSuccess"), "success");
    } else {
      toasts.toastMsg(
        nanoid(),
        resp.error.message ?? t("common.alert.fail"),
        "error"
      );
    }
  };
  /** UI: 폴더 아이템 한개씩 */
  const renderFolderItem = (item: FolderInfoDTO) => {
    return (
      <Box
        height={60}
        minHeight={60}
        alignItems="center"
        width={"100%"}
        style={{
          boxSizing: "border-box",
          paddingLeft: `${(item.parentFolder?.length ?? 0) * 12 + 8}px`,
        }}
      >
        <Box
          width={32}
          height={32}
          alignItems="center"
          classes="cursor pointer"
          onClick={() => {
            checkboxGroup.handleClick(item.folderId);
            getFolderInfo(item.folderId);
          }}
        >
          <Icons
            variant="line"
            label="chevron"
            direction={
              checkboxGroup.isSelected(item.folderId) ? "down" : "right"
            }
            size={20}
          />
        </Box>
        <Box classes="mg ml-8" minWidth={20}>
          <Icons variant="document" label="folder" />
        </Box>

        <Tooltip
          message={
            item.workgroupId === item.folderId
              ? workgroupState.workgroupDetail.workgroup.workgroupName
              : item.folderName
          }
          maxWidth={`calc(100% - ${
            (item.parentFolder?.length ?? 0) * 12
          }px - 90px)`}
        >
          <Typography type="body2_rg" ellipsis classes="mg ml-8">
            {item.workgroupId === item.folderId
              ? workgroupState.workgroupDetail.workgroup.workgroupName
              : item.folderName}
          </Typography>
        </Tooltip>
        <Radio
          check={selectFolderId === item.folderId}
          onClick={() => changeSelectFolderId(item?.folderId)}
          disable={item.folderId === currentFolderId}
          style={{ marginLeft: "auto" }}
        />
      </Box>
    );
  };
  /** UI: 폴더 트리 구조 */
  const renderRecursiveItem = (id: string) => {
    return folderInfo
      ?.filter((item1) => item1.folderId === id)
      .map((item) => (
        <Box direction="column">
          {renderFolderItem(item)}
          {checkboxGroup.isSelected(item.folderId) &&
            item.childrenFolder?.map((item) =>
              renderRecursiveItem(item.folderId)
            )}
        </Box>
      ));
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("file.action.moveToAnotherFolder")}
      subTitle={t("file.selectFolderDesc")}
      width={600}
      bodyHeight={386}
      footer={
        <Box>
          <Button
            btnStyle="fill"
            label={t("common.cancel")}
            type="secondary"
            onClick={onClose}
          />
          <Button
            btnStyle="fill"
            label={t("common.move")}
            state={selectFolderId ? "default" : "disable"}
            onClick={handleMoverFileToOtherFolder}
          />
        </Box>
      }
      body={
        <Box
          direction="column"
          width={"100%"}
          height={"100%"}
          classes="folderMoveModal"
        >
          {workgroupId && renderRecursiveItem(workgroupId)}
        </Box>
      }
    />
  );
};
export default FolderMoveModal;
